import { useCallback, useEffect, useMemo, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { type SortingState } from "@tanstack/react-table";
import { ApiError } from "@njbsoft/api-client";
import {
  buildOutboxStates,
  offlineQueueAssignments,
  partitionPoolAssignments,
  selectMyWorkBranch,
  type BlockedRequirement,
} from "@njbsoft/field-sync";
import { type components } from "@njbsoft/types/field";
import { BREAKPOINT_SM_QUERY, useMediaQuery } from "@njbsoft/form-ui";
import {
  Button,
  DataTable,
  EmptyState,
  PageHeader,
  Pagination,
  Select,
  Skeleton,
  SkeletonTable,
} from "@/components/ui";
import { PlusIcon } from "@/components/icons";
import { FieldAccessEnded } from "@/components/tester/chrome/field-access-ended";
import { FieldStatusBanner } from "@/components/tester/chrome/field-status-banner";
import { SyncBar } from "@/components/tester/chrome/sync-bar";
import {
  buildTesterBrowseColumns,
  columnIdToSortBy,
  TESTER_BROWSE_COLUMN_COUNT,
} from "@/components/tester/browse/assignment-browse-columns";
import {
  AssignmentBrowseFilterBar,
  emptyAssignmentBrowseFilters,
  hasAssignmentBrowseFilters,
  type BrowseOrgOption,
} from "@/components/tester/browse/assignment-browse-filter-bar";
import { ViewToggle, type MyWorkView } from "@/components/tester/browse/view-toggle";
import { ChangeRequestRejectionBanner } from "@/components/tester/home/change-request-rejection-banner";
import { RejectionBanner } from "@/components/tester/home/rejection-banner";
import { RequirementsBanner } from "@/components/tester/home/requirements-banner";
import { AssignmentCard } from "@/components/tester/work/assignment-card";
import { ChangeRequestsWaitingCard } from "@/components/tester/work/change-requests-waiting-card";
import { CompletedTab } from "@/components/tester/work/completed-tab";
import { CompanyPoolSection } from "@/components/tester/work/company-pool-section";
import { LinkedAccountsSection } from "@/components/tester/work/linked-accounts-section";
import { RequestsTab } from "@/components/tester/work/requests-tab";
import { UnreadMentionsBanner } from "@/components/tester/work/unread-mentions-banner";
import { deriveWorkKpis, workKpiWindows } from "@/components/tester/work/work-kpi";
import { WorkKpiRow } from "@/components/tester/work/work-kpi-row";
import { SuspensionBanner } from "@/components/utilities/suspension-banner";
import { useBundleAssignments } from "@/hooks/use-bundle-assignments";
import { useChangeRequestOutbox } from "@/hooks/use-change-request-outbox";
import { useFieldMe } from "@/hooks/use-persona";
import { useFieldworkSyncValues } from "@/lib/fieldwork/sync-provider";
import { useIsOnline } from "@/hooks/use-is-online";
import { useMySubmissions } from "@/hooks/use-my-submissions";
import { useTesterReadiness } from "@/hooks/use-test-readiness";
import { useToast } from "@/hooks/use-toast";
import {
  useMyWorkAssignments,
  type MyWorkAssignmentsParams,
  type WorkAssignmentStatus,
} from "@/hooks/use-my-work-assignments";
import { AssignmentRequirementsUnmetError } from "@/hooks/use-assignment-transition";
import { type StartRefusal } from "@/hooks/use-start-and-open";
import {
  ClaimOfflineError,
  ClaimRaceError,
  useClaimAssignment,
} from "@/hooks/use-claim-assignment";

type WorkAssignment = components["schemas"]["WorkAssignment"];

/** The Work page's three tabs. `queue` is the default a bare arrival lands on. */
export type MyWorkTab = "queue" | "completed" | "requests";

interface MyWorkSearch {
  /** The panel to open; absent means the Queue — the default a bare arrival lands on. */
  tab?: MyWorkTab;
  /** The Queue tab's own lens; absent means cards. */
  view?: MyWorkView;
}

export const Route = createFileRoute("/_company/my-work/")({
  // `tab` picks the panel; `view` is the Queue tab's own lens and is ignored
  // everywhere else — the two names cannot collide, and a link that today
  // carries `?view=cards` resolves to the Queue tab unchanged. Both members
  // stay OPTIONAL on the wire (the messages-route idiom): an arrival carrying
  // neither is the Queue with cards, and the page applies the defaults below
  // rather than the URL restating them.
  validateSearch: (search: Record<string, unknown>): MyWorkSearch => ({
    tab: search.tab === "completed" || search.tab === "requests" ? search.tab : undefined,
    view: search.view === "table" ? "table" : undefined,
  }),
  component: TesterMyWorkPage,
});

const LOADING_ROW_COUNT = 5;
const BROWSE_PAGE_SIZE = 20;
const DEFAULT_BROWSE_SORT: SortingState = [{ id: "due_date", desc: false }];
// A page of ONE is the cheapest read that still carries `total_count` — the
// number is the whole point of every KPI read below.
const KPI_COUNT_PAGE_SIZE = 1;

function ListLoadingSkeleton() {
  return (
    <div className="space-y-6 p-4 sm:p-8">
      <Skeleton width="240px" height="36px" />
      <div className="space-y-2">
        {Array.from({ length: LOADING_ROW_COUNT }).map((_, index) => (
          <Skeleton key={`work-row-${String(index)}`} height="72px" />
        ))}
      </div>
    </div>
  );
}

function ListError() {
  const { t } = useTranslation(["common"]);
  return (
    <div className="flex min-h-64 items-center justify-center p-4 sm:p-8">
      <p className="text-danger">{t("common:errors.somethingWentWrong")}</p>
    </div>
  );
}

/**
 * The tab bar's one entry: a link carrying its tab, active on its own panel. A
 * 44 px target at every width, as the view toggle's tabs below it are. Below
 * `sm` the inline padding tightens to 12 px, so the three EN tabs fit the
 * 328 px row a 360 px phone leaves without scrolling.
 */
function WorkTabLink({ tab, active, label }: { tab: MyWorkTab; active: boolean; label: string }) {
  return (
    <Link
      to="/my-work"
      search={{ tab }}
      role="tab"
      aria-selected={active}
      className={
        active
          ? "-mb-px inline-flex min-h-11 items-center border-b-2 border-primary px-4 py-2 text-sm font-semibold text-primary max-sm:px-3"
          : "-mb-px inline-flex min-h-11 items-center border-b-2 border-transparent px-4 py-2 text-sm font-medium text-muted hover:text-on-surface max-sm:px-3"
      }
    >
      {label}
    </Link>
  );
}

/**
 * THE tester work page: one address, three tabs — the Queue (assigned + in
 * progress + company pool claimable), the Completed archive, and the Change
 * Requests the tester has riding with utilities.
 *
 * The Queue tab IS the former My Work cards arm, banners and KPI row absorbed
 * from the tester home: the alarm banners outrank the work they alarm about,
 * and the four KPI numbers carry what the greeting subtitle used to. The
 * dual-source branch logic is the queue's own and decides which source serves
 * it: the field stack whenever it can be reached, the locally stored bundle
 * when it cannot. Offline the bundle is the ONLY source — both queries this
 * tab runs are dead there, and the self-context one fails outright rather
 * than hanging, so a network-only screen would render a full-page error over a
 * queue the device is holding. Which source is live is `selectMyWorkBranch`'s
 * call, never this screen's.
 *
 * The card queue pages with a "Load more" button rather than an intersection
 * sentinel — the web stand-in for mobile's `onEndReached` threshold. A sentinel
 * would fire on scroll position, which is exactly what a tester on a slow
 * connection does not want an implicit extra page for.
 *
 * `?view=table` swaps the cards for a server-paged browse table over the same
 * list. That arm is ONLINE-ONLY by design, not by omission: its paging, sorting
 * and filtering are all server work, and the stored bundle answers none of
 * them. With no connection the toggle's table tab is inert and a `?view=table`
 * link resolves to the cards — the arm the bundle does serve — with a line
 * saying so.
 */
function TesterMyWorkPage() {
  const { t } = useTranslation(["field-work", "change-requests", "common"]);
  const toast = useToast();
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState("");
  const { tab: tabParam, view: requestedView } = Route.useSearch();
  const activeTab: MyWorkTab = tabParam ?? "queue";
  const [browseFilters, setBrowseFilters] = useState(emptyAssignmentBrowseFilters);
  const [browseSorting, setBrowseSorting] = useState<SortingState>(DEFAULT_BROWSE_SORT);
  const [browseOffset, setBrowseOffset] = useState(0);

  // One clock read, shared by every window on the page — the KPI row's week,
  // the overdue bound, and the report range the Completed tab opens on.
  const [now] = useState(() => new Date());

  // The sync state comes from the shell's single provider mount — mounting the
  // hook here would register a second full set of run triggers app-wide.
  const { syncFresh, isSyncing, pendingCount, lastSyncAt, facilityCoverage, visible } =
    useFieldworkSyncValues();

  // Arriving at the queue is itself a run trigger — the one the provider's
  // app-wide mount took away when launch became per-tab: an in-app navigation
  // here fires no listener, so owed work a tester returns to the queue with
  // would never send (no click, no focus change, no new offline→online edge).
  // FRESH, not now: the fresh entry chains after any in-flight run, so residue
  // an aborted run left behind gets a new one.
  useEffect(() => {
    void syncFresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per MOUNT is the contract: the provider keeps sole ownership of the four listener classes, and a values refresh changing the syncFresh identity is not a new arrival
  }, []);

  const changeRequests = useChangeRequestOutbox();
  const outboxStates = useMemo(() => buildOutboxStates(visible), [visible]);
  // The blocked rows' retained cures, keyed for the card chips: a `blocked`
  // row carries what the tester must cure, and the card's chip names it. The
  // selection rule is `buildOutboxStates`'s — rows arrive chronologically
  // (UUIDv7 keys sort by mint time) and the LAST row per assignment is what the
  // chip describes, so a later row in any other state CLEARS the entry rather
  // than leaving a refusal standing under a newer chip.
  const blockedRequirementsByAssignment = useMemo(() => {
    const byAssignment = new Map<string, BlockedRequirement[]>();
    for (const row of visible) {
      const requirements = row.blockedRequirements ?? [];
      if (row.state === "blocked" && requirements.length > 0) {
        byAssignment.set(row.assignmentId, requirements);
      } else {
        byAssignment.delete(row.assignmentId);
      }
    }
    return byAssignment;
  }, [visible]);
  // The bar's stuck-work counts, over BOTH outbox families: submissions and
  // change-request sets (a set row carries no `revoked`). Offline these gate
  // the retained-work lines; online they are the only thing the bar renders.
  const attention = useMemo(() => {
    const count = (state: string) =>
      visible.filter((row) => row.state === state).length +
      changeRequests.setRows.filter((row) => row.state === state).length;
    return { blocked: count("blocked"), failed: count("error"), revoked: count("revoked") };
  }, [visible, changeRequests.setRows]);

  const isOffline = !useIsOnline();
  // The table arm can only be served by the network, so offline the requested
  // view yields to the cards. Falling back is a designed state — the note below
  // is the whole of the feedback, never a spinner and never an error.
  const view: MyWorkView = isOffline ? "cards" : (requestedView ?? "cards");

  // Any filter or sort change returns the browse table to its first page.
  useEffect(() => {
    setBrowseOffset(0);
  }, [browseSorting, browseFilters]);

  const browseSort = browseSorting[0];

  // The ONE place either arm's wire params are assembled. The cards carry a
  // single filter axis (status); the table carries four filters, both sort axes
  // and its page cursor. Each empty string means "every" and rides as
  // `undefined`; each non-empty status option value is a WorkAssignmentStatus.
  const queryParams = useMemo<MyWorkAssignmentsParams>(() => {
    if (view !== "table") {
      return { status: (statusFilter || undefined) as WorkAssignmentStatus | undefined };
    }
    return {
      status: (browseFilters.status || undefined) as WorkAssignmentStatus | undefined,
      org_id: browseFilters.orgId || undefined,
      due_from: browseFilters.dueFrom || undefined,
      due_to: browseFilters.dueTo || undefined,
      sort_by: browseSort ? columnIdToSortBy[browseSort.id] : undefined,
      sort_dir: browseSort ? (browseSort.desc ? "desc" : "asc") : undefined,
      limit: BROWSE_PAGE_SIZE,
      offset: browseOffset,
    };
  }, [view, statusFilter, browseFilters, browseSort, browseOffset]);

  const meQuery = useFieldMe();
  const listQuery = useMyWorkAssignments(queryParams);
  const { hasNextPage, isFetchingNextPage, fetchNextPage, refetch: refetchList } = listQuery;
  const { refetch: refetchMe } = meQuery;

  const bundle = useBundleAssignments();

  // The KPI row's four numbers, each off the tabs' own reads: three assignment
  // windows through the same list hook the queue uses, and the awaiting-review
  // count through the same archive hook the Completed tab uses — total_count
  // over a page of ONE, never a parallel stat fetch. The week window starts
  // TODAY and the overdue bound is yesterday, so no open row lands in both
  // tiles; a read that has not landed states a dash, not a zero.
  const kpiWindows = useMemo(() => workKpiWindows(now), [now]);
  const dueWeekQuery = useMyWorkAssignments({
    open: true,
    due_from: kpiWindows.dueFrom,
    due_to: kpiWindows.dueTo,
    limit: KPI_COUNT_PAGE_SIZE,
  });
  const overdueQuery = useMyWorkAssignments({
    open: true,
    due_to: kpiWindows.overdueTo,
    limit: KPI_COUNT_PAGE_SIZE,
  });
  const inProgressQuery = useMyWorkAssignments({
    status: "in_progress",
    limit: KPI_COUNT_PAGE_SIZE,
  });
  const awaitingReviewQuery = useMySubmissions({
    status: "needs_review",
    limit: KPI_COUNT_PAGE_SIZE,
  });
  const kpis = deriveWorkKpis({
    dueWeekTotal: dueWeekQuery.data?.pages[0]?.total_count ?? null,
    overdueTotal: overdueQuery.data?.pages[0]?.total_count ?? null,
    inProgressTotal: inProgressQuery.data?.pages[0]?.total_count ?? null,
    awaitingReviewTotal: awaitingReviewQuery.data?.total_count ?? null,
  });

  // The ONE tester-mode readiness query this page runs: each card renders its
  // utility's verdict block from it, joined by org_id below. Offline it errors
  // and the cards render without verdicts — the stored arm's chips are the
  // outbox ones, and the offline start gate (its own hook) is where a stale
  // block is re-checked.
  const readinessQuery = useTesterReadiness();
  const requirementsByOrg = useMemo(() => {
    const byOrg = new Map<string, components["schemas"]["RequirementVerdict"][]>();
    for (const utility of readinessQuery.data?.utilities ?? []) {
      byOrg.set(utility.org_id, utility.requirements);
    }
    return byOrg;
  }, [readinessQuery.data]);

  const items = useMemo(
    () => listQuery.data?.pages.flatMap((page) => page.items) ?? [],
    [listQuery.data],
  );
  // The stored rows carry the same wire shape, so both sources partition and
  // render through the same code; the status filter is applied here because
  // offline there is no server to apply it.
  const storedItems = useMemo(
    () => offlineQueueAssignments(bundle.rows, queryParams.status),
    [bundle.rows, queryParams.status],
  );

  // One fetched list, two views: the tester's own queue feeds the cards, the
  // company-pool rows feed the "Available from <company>" section above them.
  const fetched = useMemo(() => partitionPoolAssignments(items), [items]);
  const stored = useMemo(() => partitionPoolAssignments(storedItems), [storedItems]);

  const claimMutation = useClaimAssignment();
  const [claimingId, setClaimingId] = useState<string | null>(null);
  // The server's claim refusal, held for the pool row that provoked it: the
  // failing requirements with their cures, rendered by the row — never only a
  // toast, which expires into a retry the server refuses again.
  const [claimRefusal, setClaimRefusal] = useState<StartRefusal | null>(null);
  const { mutate: claim } = claimMutation;

  const handleClaim = useCallback(
    (assignment: WorkAssignment) => {
      setClaimingId(assignment.id);
      setClaimRefusal(null);
      claim(
        { assignmentId: assignment.id },
        {
          onSuccess: async () => {
            toast.success("field-work:pool.claimed");
            // Pull the just-claimed assignment's bundle row before execution;
            // syncFresh chains a fresh pull after any in-flight coalesced run.
            // The queue itself refetches off the mutation's own invalidation.
            await syncFresh();
            // Only THEN open the row: landing on the detail before its bundle
            // row exists is what the fresh pull prevents.
            await navigate({
              to: "/my-work/$assignmentId",
              params: { assignmentId: assignment.id },
            });
          },
          onError: (err) => {
            if (err instanceof ClaimRaceError) {
              toast.warning("field-work:pool.alreadyClaimed");
              void refetchList();
            } else if (err instanceof ClaimOfflineError) {
              toast.warning("field-work:pool.offlineNote");
            } else if (err instanceof AssignmentRequirementsUnmetError) {
              setClaimRefusal({ assignmentId: assignment.id, failing: err.failing });
            } else {
              toast.apiError(err instanceof ApiError ? err.code : undefined);
            }
          },
          // The spinner is this screen's state, not the mutation's — the hook's
          // own onSettled invalidates the queue and never touches it.
          onSettled: () => setClaimingId(null),
        },
      );
    },
    [claim, toast, syncFresh, refetchList, navigate],
  );

  const branch = selectMyWorkBranch({
    meQuery: {
      isLoading: meQuery.isLoading,
      isError: meQuery.isError,
      error: meQuery.error,
      data: meQuery.data,
    },
    listQuery: {
      isLoading: listQuery.isLoading,
      isError: listQuery.isError,
      // The branch's empty state covers both partitions; a pool-only screen
      // must not collapse to the no-assignments view.
      itemCount: fetched.mine.length + fetched.pool.length,
    },
    offline: { isOffline, isLoaded: bundle.isLoaded },
  });

  const isStoredArm = branch === "offlineList";
  const { mine, pool } = isStoredArm ? stored : fetched;

  const handleLoadMore = useCallback(() => {
    if (hasNextPage) void fetchNextPage();
  }, [hasNextPage, fetchNextPage]);

  // Refresh pulls BOTH queries. `me` is the only one that can clear a stale
  // noActiveGrants, so a list-only refresh would leave a tester whose grant was
  // just reinstated locked out of their own queue.
  const handleRefresh = useCallback(() => {
    void Promise.all([refetchMe(), refetchList()]);
  }, [refetchMe, refetchList]);

  const filterOptions = useMemo(
    () => [
      { value: "", label: t("field-work:myWork.filterAll") },
      { value: "assigned", label: t("field-work:myWork.filterAssigned") },
      { value: "in_progress", label: t("field-work:myWork.filterInProgress") },
    ],
    [t],
  );

  const handleNewInstallation = useCallback(() => {
    void navigate({ to: "/my-work/new-installation" });
  }, [navigate]);

  const handleViewChange = useCallback(
    (next: MyWorkView) => {
      void navigate({ to: "/my-work", search: { view: next } });
    },
    [navigate],
  );

  const openAssignment = useCallback(
    (row: WorkAssignment) => {
      void navigate({
        to: "/my-work/$assignmentId",
        params: { assignmentId: row.id },
      });
    },
    [navigate],
  );

  // Below sm the browse table leads each row with its Open action: the table
  // scrolls inside its own box there, and a trailing Open would sit past the
  // phone's edge. One breakpoint picks the column order, so the rows render once.
  const fromSm = useMediaQuery(BREAKPOINT_SM_QUERY, true);
  const browseColumns = useMemo(
    () => buildTesterBrowseColumns(t, openAssignment, { actionFirst: !fromSm }),
    [t, openAssignment, fromSm],
  );

  // The utility dropdown's only admissible source: the caller's own granting
  // authorizations. A suspended or revoked grant returns no rows, so offering
  // it would be a filter that can only empty the table.
  const orgOptions = useMemo<BrowseOrgOption[]>(() => {
    const seen = new Set<string>();
    const options: BrowseOrgOption[] = [];
    for (const auth of meQuery.data?.authorizations ?? []) {
      if (auth.status !== "active" || seen.has(auth.org_id)) continue;
      seen.add(auth.org_id);
      options.push({ id: auth.org_id, name: auth.org_name });
    }
    return options;
  }, [meQuery.data]);

  const resetBrowseFilters = useCallback(() => {
    setBrowseFilters(emptyAssignmentBrowseFilters);
  }, []);

  if (branch === "accessEnded") return <FieldAccessEnded />;
  if (branch === "loading") return <ListLoadingSkeleton />;
  if (branch === "error") return <ListError />;

  const authorizations = meQuery.data?.authorizations ?? [];
  // Every row the filter matched, not the page's length — the pagination
  // arithmetic is the only consumer.
  const totalCount = listQuery.data?.pages[0]?.total_count ?? 0;

  if (branch === "noActiveGrants") {
    return (
      <div className="overflow-y-auto p-4 sm:p-8">
        <FieldStatusBanner authorizations={authorizations} />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 sm:p-8">
      {/* Refresh refetches the queries the queue arm is not reading. Offline it
          could change nothing on screen even if a request slipped through, so
          the arm drops it; the sync bar's depth line is the honest offline
          affordance (its Sync Now is disabled — the connectivity-regain
          trigger owns the drain). New installation stays: the
          intake is captured on this device and sends by itself. */}
      <PageHeader
        title={t("field-work:myWork.title")}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" onClick={handleNewInstallation}>
              <PlusIcon className="h-4 w-4" aria-hidden="true" />
              {t("change-requests:kind.new_installation")}
            </Button>
            {activeTab === "queue" && !isStoredArm ? (
              <Button variant="secondary" onClick={handleRefresh}>
                {t("field-work:myWork.refresh")}
              </Button>
            ) : null}
          </div>
        }
      />

      {/* One row that scrolls inside its own box when the labels outrun a
          phone (Spanish needs about 460 px). The row is as wide as its tabs
          and carries the border, so the underline baseline runs under every
          tab and the tabs' 1 px overlap of it stays inside the scroll clip. */}
      <div className="overflow-x-auto print:hidden">
        <div className="flex w-max min-w-full gap-1 border-b border-gray-200 dark:border-gray-700">
          <WorkTabLink
            tab="queue"
            active={activeTab === "queue"}
            label={t("field-work:myWork.tabs.queue")}
          />
          <WorkTabLink
            tab="completed"
            active={activeTab === "completed"}
            label={t("field-work:myWork.tabs.completed")}
          />
          <WorkTabLink
            tab="requests"
            active={activeTab === "requests"}
            label={t("field-work:myWork.tabs.requests")}
          />
        </div>
      </div>

      {activeTab === "completed" ? (
        isOffline ? (
          // The bundle answers none of the archive's filters, so offline the
          // tab is inert with a note — exactly the server-only-table posture
          // the standalone page held.
          <EmptyState
            title={t("field-work:history.offline.title")}
            description={t("field-work:history.offline.body")}
          />
        ) : (
          <CompletedTab now={now} />
        )
      ) : activeTab === "requests" ? (
        <RequestsTab />
      ) : (
        <>
          {/* The banner row, in alarm order: the suspension stops the work,
              the sync bar reports what is held for it, then the three
              tester alarms the home page used to carry, then the unread
              mentions slot. Self-gating components throughout — an alarm with
              nothing to say renders nothing. */}
          <div className="space-y-4 print:hidden">
            <SuspensionBanner />
            <SyncBar
              pendingCount={pendingCount}
              changeRequestSetRows={changeRequests.setRows}
              lastSyncAt={lastSyncAt}
              isSyncing={isSyncing}
              attention={attention}
              facilityCoverage={facilityCoverage}
            />
            <RequirementsBanner now={now} />
            <RejectionBanner />
            <ChangeRequestRejectionBanner />
            <UnreadMentionsBanner />
          </div>

          <WorkKpiRow kpis={kpis} />

          {/* Above the view toggle and the Status filter, which act on the
              tester's assignments only: below them the section would read as
              something they filter or switch away. Self-gating — nothing
              offline and nothing without a live link. */}
          <LinkedAccountsSection />

          <ViewToggle value={view} onChange={handleViewChange} tableDisabled={isOffline} />
          {/* Every card action writes a row back to the queue first, so it runs a fresh sync. */}
          <ChangeRequestsWaitingCard changeRequests={changeRequests} onSyncNow={syncFresh} />
          {/* The banner reads authorizations, and the stored arm has none to read:
              `/field/me` is their only source and nothing persists them. Mounted
              with an empty list it would declare the tester's grants inactive —
              the one thing that cannot be known with no connection. */}
          {isStoredArm ? null : <FieldStatusBanner authorizations={authorizations} />}
          {view === "table" ? (
            <div className="space-y-4">
              <AssignmentBrowseFilterBar
                value={browseFilters}
                orgs={orgOptions}
                onChange={setBrowseFilters}
              />
              {/* The page's own loading branch covers the cold start; this one is
                  the in-arm case — a page whose predecessor held no rows to keep. */}
              {listQuery.isFetching && items.length === 0 ? (
                <SkeletonTable columns={TESTER_BROWSE_COLUMN_COUNT} />
              ) : items.length === 0 ? (
                hasAssignmentBrowseFilters(browseFilters) ? (
                  <EmptyState
                    title={t("field-work:myWork.browse.emptyFiltered.title")}
                    description={t("field-work:myWork.browse.emptyFiltered.description")}
                    actionLabel={t("field-work:myWork.browse.filters.reset")}
                    onAction={resetBrowseFilters}
                  />
                ) : (
                  <EmptyState
                    title={t("field-work:myWork.empty.title")}
                    description={t("field-work:myWork.empty.description")}
                  />
                )
              ) : (
                <>
                  {/* Unpartitioned on purpose: `total_count` counts every row the
                      filter matched, so splitting the pool out of the page would
                      put the pagination arithmetic at odds with what is on screen.
                      Claiming stays on the card arm, where the pool section is. */}
                  <DataTable
                    columns={browseColumns}
                    data={items}
                    sorting={browseSorting}
                    onSortingChange={setBrowseSorting}
                    onRowClick={openAssignment}
                  />
                  <Pagination
                    currentPage={Math.floor(browseOffset / BROWSE_PAGE_SIZE) + 1}
                    totalPages={Math.max(1, Math.ceil(totalCount / BROWSE_PAGE_SIZE))}
                    totalItems={totalCount}
                    pageSize={BROWSE_PAGE_SIZE}
                    onPageChange={(page) => setBrowseOffset((page - 1) * BROWSE_PAGE_SIZE)}
                  />
                </>
              )}
            </div>
          ) : (
            <>
              <div className="w-48">
                <label
                  htmlFor="my-work-status"
                  className="mb-1 block text-sm font-medium text-on-surface"
                >
                  {t("field-work:myWork.filterStatus")}
                </label>
                <Select
                  id="my-work-status"
                  className="min-h-11"
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value)}
                  options={filterOptions}
                />
              </div>
              <CompanyPoolSection
                pool={pool}
                claimingId={claimingId}
                onClaim={handleClaim}
                refusal={claimRefusal}
              />
              {/* Only say "no assignments" when BOTH partitions are empty — a
                  pool-only screen keeps the claimable rows visible with nothing
                  buried under an empty-state card. */}
              {mine.length === 0 && pool.length === 0 ? (
                <EmptyState
                  title={t("field-work:myWork.empty.title")}
                  description={t("field-work:myWork.empty.description")}
                />
              ) : (
                <div className="space-y-2">
                  {mine.map((item) => (
                    <AssignmentCard
                      key={item.id}
                      item={item}
                      outboxState={outboxStates.get(item.id) ?? null}
                      blockedRequirements={blockedRequirementsByAssignment.get(item.id) ?? null}
                      requirements={requirementsByOrg.get(item.org_id) ?? null}
                      changeRequests={changeRequests}
                    />
                  ))}
                </div>
              )}
              {/* The stored snapshot is whole — there is no next page of it, and the
                  fetched query's page cursor cannot advance with no connection. */}
              {!isStoredArm && hasNextPage ? (
                <Button variant="secondary" onClick={handleLoadMore} loading={isFetchingNextPage}>
                  {t("field-work:myWork.loadMore")}
                </Button>
              ) : null}
            </>
          )}
        </>
      )}
    </div>
  );
}
